import clsx from "clsx";
import type { BridgeStatusDto, SiteStatusDto } from "../types/api";
import { relativeTime } from "../lib/relativeTime";
import { useNow } from "../hooks/useNow";
import BridgeRow from "./BridgeRow";
import StatusBadge from "./StatusBadge";

interface SiteCardProps {
  site: SiteStatusDto;
  /** Server-side response generation timestamp, used as relative-time anchor for BridgeRow first paint. */
  referenceNow?: string | null;
  /**
   * Bridges already filtered + sorted by the parent (`StatusPage`). When
   * undefined, falls back to `site.bridges` (original order). Pass an empty
   * array when the parent wants to render a "no matches" placeholder.
   */
  bridges?: BridgeStatusDto[];
  /**
   * When true, all bridges were filtered out by the current FilterBar selection
   * (as opposed to the site genuinely having no data). Drives the placeholder
   * copy.
   */
  filteredEmpty?: boolean;
}

type AccordionTone = "urgent" | "normal" | "gray";

const ACCORDION_TONES: Record<
  AccordionTone,
  { dot: string; label: string; badge: string }
> = {
  // DOWN/UNKNOWN — 주의가 필요한 그룹. 빨강 dot + 빨강 톤 배지로 강조.
  urgent: {
    dot: "bg-status-majorOutage dark:bg-status-majorOutage-dark",
    label: "font-semibold text-status-majorOutage-fg dark:text-status-majorOutage-darkFg",
    badge:
      "bg-status-majorOutage-bg text-status-majorOutage-fg dark:bg-status-majorOutage-darkBg dark:text-status-majorOutage-darkFg",
  },
  // UP 그룹 — 정상 신호를 살짝 — summary 좌측에 초록 dot + 초록 톤 배지.
  normal: {
    dot: "bg-status-operational dark:bg-status-operational-dark",
    label: "text-slate-600 dark:text-slate-300",
    badge:
      "bg-status-operational-bg text-status-operational-fg dark:bg-status-operational-darkBg dark:text-status-operational-darkFg",
  },
  // STALE / RETIRED — 가라앉히는 회색.
  gray: {
    dot: "bg-slate-300 dark:bg-slate-600",
    label: "text-slate-500 dark:text-slate-400",
    badge:
      "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  },
};

/**
 * Collapsible group using the native `<details>` element so keyboard,
 * screen reader, and right-click "find in page" all work out of the box —
 * no manual ARIA state management. Chevron rotation via Tailwind's
 * `group-open:` variant.
 *
 * `tone` controls the dot/badge color — UP gets a subtle green accent so the
 * "정상" group is identifiable at a glance even when collapsed.
 */
function GroupAccordion({
  title,
  count,
  defaultOpen = false,
  tone = "gray",
  children,
}: {
  title: string;
  count: number;
  defaultOpen?: boolean;
  tone?: AccordionTone;
  children: React.ReactNode;
}) {
  const t = ACCORDION_TONES[tone];
  return (
    <details
      className={clsx(
        "group border-t",
        "border-surface-border dark:border-surface-darkBorder",
      )}
      {...(defaultOpen ? { open: true } : {})}
    >
      <summary
        className={clsx(
          "cursor-pointer list-none select-none",
          "flex items-center justify-between gap-3 px-5 py-3 sm:px-6",
          "hover:bg-slate-50 dark:hover:bg-slate-800/30",
          // hide default disclosure triangle on Safari/Chrome/Firefox
          "[&::-webkit-details-marker]:hidden [&::marker]:hidden",
        )}
      >
        <span className="flex items-center gap-2 text-sm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 12 12"
            fill="currentColor"
            className="h-3 w-3 flex-shrink-0 text-slate-400 transition-transform duration-150 group-open:rotate-90 dark:text-slate-500"
            aria-hidden="true"
          >
            <path d="M4 2.5L8.5 6L4 9.5V2.5Z" />
          </svg>
          <span
            aria-hidden="true"
            className={clsx(
              "inline-block h-2 w-2 flex-shrink-0 rounded-full",
              t.dot,
            )}
          />
          <span className={clsx("font-medium", t.label)}>{title}</span>
        </span>
        <span
          className={clsx(
            "rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums",
            t.badge,
          )}
        >
          {count}개
        </span>
      </summary>
      <ul
        className={clsx(
          "divide-y-0 border-t",
          "border-surface-border dark:border-surface-darkBorder",
        )}
      >
        {children}
      </ul>
    </details>
  );
}

/**
 * Site card — one of three for hoban / integration / ansan.
 *
 * Header row: displayName + dbType subtle badge | last poll relative time | status badge
 *
 * Body — all four groups are collapsed-by-default `<details>` accordions:
 *   1. Urgent group (DOWN / UNKNOWN) — 빨강 dot + 톤. 사용자가 직접 펼쳐 확인.
 *   2. Normal group (UP) — 초록 dot.
 *   3. STALE group (미운영, 60일+ no data) — 회색.
 *   4. RETIRED group (운영 종료, pkl_st='02') — 회색.
 *
 * 초기 상태는 모든 그룹이 접혀 있다 (요청). 종합 상태 배지와 카운트는 헤더로 노출되므로
 * 카드만 보고도 "어느 그룹에 몇 개" 가 파악된다. 펼침 상태는 native `<details>` 가
 * 페이지 내 유지하지만 페이지 새로고침 시에는 모두 접힘으로 돌아온다.
 */
export default function SiteCard({
  site,
  referenceNow,
  bridges,
  filteredEmpty = false,
}: SiteCardProps) {
  const now = useNow();
  const visibleBridges = bridges ?? site.bridges;

  // Partition by status into four buckets. The incoming `bridges` array is
  // already sorted by the parent (triage / name / updated / etc.); we preserve
  // that order within each bucket via stable iteration.
  const urgentBridges: BridgeStatusDto[] = [];  // DOWN + UNKNOWN
  const normalBridges: BridgeStatusDto[] = [];  // UP
  const staleBridges: BridgeStatusDto[] = [];
  const retiredBridges: BridgeStatusDto[] = [];
  for (const b of visibleBridges) {
    // lot 분류는 브릿지 DB 헬스(b.status)만으로 한다. 영상분석 mismatch 는 lot 을
    // 긴급으로 승격하지 않고(약한 주황 경고), BridgeRow/ZoneList 에서만 가볍게 표시한다.
    switch (b.status) {
      case "DOWN":
      case "UNKNOWN":
        urgentBridges.push(b);
        break;
      case "UP":
        normalBridges.push(b);
        break;
      case "STALE":
        staleBridges.push(b);
        break;
      case "RETIRED":
        retiredBridges.push(b);
        break;
    }
  }

  const hasAny = visibleBridges.length > 0;

  // Anchor relative time to `now` (1s tick) instead of `referenceNow` so the
  // "마지막 폴링 N초 전" label keeps moving between refetches.
  const lastPolledLabel = relativeTime(site.lastPolledAt, {
    referenceNow: now,
  });

  return (
    <article
      className={clsx(
        "overflow-hidden rounded-2xl border bg-surface shadow-sm",
        "border-surface-border dark:border-surface-darkBorder",
        "dark:bg-surface-darkSubtle",
      )}
      aria-label={`${site.displayName} 상태`}
    >
      <header
        className={clsx(
          "flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6",
          "border-b border-surface-border dark:border-surface-darkBorder",
        )}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {site.displayName}
          </h3>
          <span
            className={clsx(
              "rounded-md px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide",
              "bg-slate-100 text-slate-600",
              "dark:bg-slate-800 dark:text-slate-400",
            )}
            title={`siteKey: ${site.siteKey}`}
          >
            {site.dbType}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <span
            className="text-xs text-slate-500 tabular-nums dark:text-slate-400"
            title={site.lastPolledAt ?? "한 번도 폴링되지 않음"}
          >
            마지막 폴링 {lastPolledLabel}
          </span>
          <StatusBadge status={site.status} kind="overall" size="md" />
        </div>
      </header>

      {!hasAny ? (
        <div className="px-5 py-8 text-center text-sm text-slate-500 dark:text-slate-400 sm:px-6">
          {filteredEmpty
            ? "조건에 맞는 브릿지가 없습니다."
            : "아직 데이터가 수집되지 않았습니다."}
        </div>
      ) : (
        <>
          {urgentBridges.length > 0 && (
            <GroupAccordion
              title="주의 필요 (데이터 정체 · 확인 중)"
              count={urgentBridges.length}
              tone="urgent"
              defaultOpen
            >
              {urgentBridges.map((bridge) => (
                <BridgeRow
                  key={bridge.lotId}
                  bridge={bridge}
                  referenceNow={referenceNow}
                />
              ))}
            </GroupAccordion>
          )}

          {normalBridges.length > 0 && (
            <GroupAccordion
              title="정상"
              count={normalBridges.length}
              tone="normal"
            >
              {normalBridges.map((bridge) => (
                <BridgeRow
                  key={bridge.lotId}
                  bridge={bridge}
                  referenceNow={referenceNow}
                />
              ))}
            </GroupAccordion>
          )}

          {staleBridges.length > 0 && (
            <GroupAccordion
              title="미운영 (60일+ 데이터 없음)"
              count={staleBridges.length}
            >
              {staleBridges.map((bridge) => (
                <BridgeRow
                  key={bridge.lotId}
                  bridge={bridge}
                  referenceNow={referenceNow}
                />
              ))}
            </GroupAccordion>
          )}

          {retiredBridges.length > 0 && (
            <GroupAccordion
              title="운영 종료"
              count={retiredBridges.length}
            >
              {retiredBridges.map((bridge) => (
                <BridgeRow
                  key={bridge.lotId}
                  bridge={bridge}
                  referenceNow={referenceNow}
                />
              ))}
            </GroupAccordion>
          )}
        </>
      )}
    </article>
  );
}
